// src/PropertyModal.js

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import './PropertyModal.css';
import ImageCarousel from './components/ImageCarousel';
import PropertyDetailsContent from './components/PropertyDetailsContent';
import { INVESTMENT_TYPE_COLORS } from './config/filters';
import { STATUS_CONFIG } from './config/statusConfig';
import { API_URL } from './config/api';
import useSaveProperty from './hooks/useSaveProperty';
import { Heart } from 'lucide-react';

const PropertyModal = ({ property: initialProperty, onClose }) => {
  const id = initialProperty.property_id;
  const { saved, saving, toggleSave } = useSaveProperty(id);

  // Fetch full details to get AI analysis etc.
  const { data: fullProperty } = useQuery({
    queryKey: ['property', id],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/properties/${id}`);
      if (!response.ok) throw new Error('Failed to fetch property details');
      return response.json();
    },
    initialData: initialProperty,
    staleTime: 0,
  });

  const property = fullProperty || initialProperty;

  const handleContentClick = (e) => {
    e.stopPropagation();
  };

  const images = property.photos && property.photos.length > 0
    ? property.photos
    : (property.primary_photo_url ? [property.primary_photo_url] : []);

  const status = property.listing_status || 'FOR_SALE';
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.UNKNOWN;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="property-modal-content" onClick={handleContentClick}>
        <button className="modal-close-btn" onClick={onClose} aria-label="Close modal">×</button>

        {/* Image Carousel */}
        <div className="modal-image-section">
          <ImageCarousel images={images} alt={`Exterior of ${property.address}`} variant="blur" />

          {/* Status Chip */}
          <div
            className="modal-status-chip"
            style={{ backgroundColor: statusConfig.bg, color: statusConfig.color, borderColor: statusConfig.color }}
          >
            {statusConfig.label}
          </div>

          {/* Investment Type Tags */}
          {property.investment_types && property.investment_types.length > 0 && (
            <div className="modal-tags-overlay">
              {property.investment_types.map((type, idx) => (
                <span
                  key={idx}
                  className="modal-investment-tag"
                  style={{ backgroundColor: INVESTMENT_TYPE_COLORS[type] || '#6b7280' }}
                >
                  {type}
                </span>
              ))}
            </div>
          )}

          {/* Heart / Save Button */}
          <button
            className={`modal-save-btn ${saved ? 'saved' : ''}`}
            onClick={toggleSave}
            aria-label={saved ? 'Unsave property' : 'Save property'}
            disabled={saving}
          >
            <Heart
              size={24}
              fill={saved ? '#ef4444' : 'none'}
              stroke={saved ? '#ef4444' : 'white'}
              strokeWidth={2}
            />
          </button>
        </div>

        {/* Shared Content */}
        <div className="modal-details-section">
          <PropertyDetailsContent property={property} />

          <div className="modal-actions" style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
            <a href={`/properties/${property.property_id}`} className="btn-primary" style={{ padding: '12px 24px', borderRadius: '8px', fontSize: '15px', textDecoration: 'none', fontWeight: '600', transition: 'transform 0.1s, background-color 0.2s' }}>
              View Full Property Details
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyModal;